// ProjectNew "KPI Seti" dropdown'u + K28 uyarısı — saf yardımcılar (RK13 / E9 bileşen testi).
// Kopya TEK kaynak: ProjectNew satır içi dönem/KPI string'i yazmaz (period-ui.ts kalıbı).
// K28: müşterinin seti VARKEN set seçilmeden "Projeyi Oluştur" basılırsa onay diyaloğu;
// set yokken diyalog HİÇ açılmaz (e2e finishWizard tek tıkla waitForURL bekler — RK13).
// TODO-KPI-12: ProjectDetail "KPI seti" alanı (ProjectKpiSetField) kopyası da burada (`detail`).

import type { ProjectStatus } from "@mahir/shared";

export const KPI_PROJECT_COPY = {
  label: "KPI Veri Seti",
  optional: "(opsiyonel)",
  noneOption: "KPI veri seti yok — raporda KPI tablosu olmaz",
  hint: "Seçilen veri setinin göstergeleri AI üretimine KPI tablosu olarak girer ve DOCX raporun sonunda “Performans Göstergeleri” bölümü olarak yer alır.",
  confirmTitle: "KPI veri seti seçilmedi",
  confirmBody:
    "Bu müşterinin KPI veri seti var, raporda KPI tablosu olmayacak. Veri seti seçmeden devam edilsin mi?",
  confirmLabel: "Veri seti olmadan devam et",
  cancelLabel: "Geri dön",
  summaryNone: "Seçilmedi",
  // TODO-KPI-29 — set ↔ çerçeve uyumu (engel değil, sıralama + rozet + amber not)
  compatibleBadge: "(uyumlu)",
  frameworkMismatch: (setFramework: string, projectFramework: string): string =>
    `Seçilen veri seti "${setFramework}" çerçevesine bağlı; proje "${projectFramework}" çerçevesiyle açılıyor. Devam edebilirsiniz — raporda veri setinin kendi çerçeve adı yazılır.`,
  // TODO-KPI-12 — ProjectDetail "KPI seti" alanı
  detail: {
    heading: "KPI veri seti",
    none: "Bağlı değil — raporda KPI tablosu olmaz",
    change: "Değiştir",
    bind: "Veri seti bağla",
    unlink: "Bağı kaldır",
    save: "Kaydet",
    cancel: "Vazgeç",
    saving: "Kaydediliyor…",
    loading: "Veri setleri yükleniyor…",
    noSets: "Bu müşterinin KPI veri seti yok.",
    readOnly: "Tamamlanmış projede KPI veri seti değiştirilemez; önce projeyi yeniden açın.",
    effect:
      "Değişiklik form yanıtlarını ve mevcut belgeleri etkilemez; sonraki AI üretimi ve DOCX yeni veri setini kullanır.",
    unlinkTitle: "KPI veri seti bağı kaldırılsın mı?",
    unlinkBody:
      "Proje KPI veri seti olmadan kalır; sonraki AI üretimi ve DOCX raporda KPI tablosu olmaz. Mevcut belgeler değişmez.",
    failed: "KPI veri seti güncellenemedi",
  },
} as const;

/** TODO-KPI-12: tamamlanmış (arşiv) projede KPI seti salt okunur — backend 409 project_completed. */
export function isProjectKpiSetEditable(status: ProjectStatus): boolean {
  return status !== "completed";
}

/**
 * TODO-KPI-12: alanın özet satırı — "Entegre 2025 · TSRS" (setin çerçevesi biliniyorsa),
 * yalnız ad, ya da bağlı değilken `detail.none`.
 */
export function projectKpiSetLabel(
  project: { kpiSetId: string | null; kpiSetName: string | null },
  set?: Pick<KpiSetOption, "frameworkName"> | null,
): string {
  if (!project.kpiSetId) return KPI_PROJECT_COPY.detail.none;
  const name = project.kpiSetName ?? "";
  return set?.frameworkName ? `${name} · ${set.frameworkName}` : name;
}

export interface KpiSetOption {
  id: string;
  name: string;
  indicatorCount: number;
  /** TODO-KPI-29: setin çerçevesi (null = çerçevesiz). Eski çağıranlar vermeyebilir. */
  frameworkId?: string | null;
  frameworkName?: string | null;
}

/** Set, projenin çerçevesine bağlı mı (her ikisi de dolu ve eşit). */
export function isKpiSetCompatible(
  set: Pick<KpiSetOption, "frameworkId">,
  projectFrameworkId: string | null | undefined,
): boolean {
  return Boolean(projectFrameworkId) && set.frameworkId === projectFrameworkId;
}

/**
 * Dropdown seçenek metni: "Entegre 2025 (12 gösterge)"; projenin çerçevesiyle eşleşen set
 * "(uyumlu)" eki alır (TODO-KPI-29). Çerçeve verilmezse eski metin birebir.
 */
export function kpiSetOptionLabel(set: KpiSetOption, projectFrameworkId?: string | null): string {
  const base = `${set.name} (${String(set.indicatorCount)} gösterge)`;
  return isKpiSetCompatible(set, projectFrameworkId)
    ? `${base} ${KPI_PROJECT_COPY.compatibleBadge}`
    : base;
}

/** Uyumlu setler önce (kendi içlerinde orijinal sıra korunur — kararlı sıralama). */
export function sortKpiSetsForFramework<T extends KpiSetOption>(
  sets: readonly T[],
  projectFrameworkId: string | null | undefined,
): T[] {
  if (!projectFrameworkId) return [...sets];
  const compatible = sets.filter((s) => isKpiSetCompatible(s, projectFrameworkId));
  const rest = sets.filter((s) => !isKpiSetCompatible(s, projectFrameworkId));
  return [...compatible, ...rest];
}

/**
 * Amber uyarı metni: seçili set BAŞKA bir çerçeveye bağlıysa (her iki çerçeve de dolu ve farklı).
 * Çerçevesiz set ya da çerçevesi bilinmeyen proje → null (uyarı yok, engel zaten yok).
 */
export function kpiSetFrameworkWarning(
  set: KpiSetOption | null,
  project: { frameworkId: string | null | undefined; frameworkName: string | null | undefined },
): string | null {
  if (!set?.frameworkId || !set.frameworkName) return null;
  if (!project.frameworkId || !project.frameworkName) return null;
  if (set.frameworkId === project.frameworkId) return null;
  return KPI_PROJECT_COPY.frameworkMismatch(set.frameworkName, project.frameworkName);
}

/**
 * K28 kapısı: müşterinin en az bir seti var VE seçim boş → onay diyaloğu.
 * Seçim listede olmayan bir id ise (müşteri değişti, bayat seçim) boş sayılır.
 */
export function shouldConfirmWithoutKpiSet(args: {
  sets: readonly KpiSetOption[];
  selectedKpiSetId: string;
}): boolean {
  if (args.sets.length === 0) return false;
  return !args.sets.some((s) => s.id === args.selectedKpiSetId);
}

/** Dropdown müşteri seçildikten sonra ve yalnız set varken render edilir. */
export function showKpiSetDropdown(args: {
  clientId: string;
  sets: readonly KpiSetOption[];
}): boolean {
  return args.clientId.length > 0 && args.sets.length > 0;
}
